import React from "react";
import PolicyLayout, { PolicyH2, PolicyP, PolicyUL, PolicyCallout } from "../components/app/PolicyLayout";

const TOC = [
  { id: "who-we-are", label: "1. Who we are" },
  { id: "using-app", label: "2. Using the app" },
  { id: "accounts", label: "3. Your account" },
  { id: "community", label: "4. Community rules" },
  { id: "pro", label: "5. CampCompass Pro" },
  { id: "affiliate", label: "6. Affiliate links" },
  { id: "content", label: "7. Your content" },
  { id: "ai", label: "8. Ask Camper (AI)" },
  { id: "liability", label: "9. Our liability" },
  { id: "changes", label: "10. Changes" },
  { id: "law", label: "11. Law & contact" },
];

export default function Terms() {
  return (
    <PolicyLayout
      title="Terms of Service"
      eyebrow="Legal · Plain English"
      lastUpdated="23 April 2026"
      toc={TOC}
      testid="terms-page"
    >
      <PolicyP>
        Hi, and welcome. These are the terms that apply when you use CampCompass UK. We've written them in plain
        English on purpose — if anything still feels unclear, email <a href="mailto:hello@camp-compass.uk" className="link-underline text-[#ff4500]">hello@camp-compass.uk</a> and we'll explain.
      </PolicyP>
      <PolicyP>
        By creating an account, posting in the community, or using the app, you agree to these terms.
      </PolicyP>

      <PolicyH2 id="who-we-are">1. Who we are</PolicyH2>
      <PolicyP>
        CampCompass UK ("we", "us", "our") is a sole trader business operated by <strong>Anthony Slater</strong>,
        based at <strong>10 Cyprus Street, Darwen, Lancashire, BB3 2JX, United Kingdom</strong>. You can reach us
        at <a href="mailto:hello@camp-compass.uk" className="link-underline text-[#ff4500]">hello@camp-compass.uk</a>.
      </PolicyP>

      <PolicyH2 id="using-app">2. Using the app</PolicyH2>
      <PolicyP>
        The app is free for anyone aged 13+ to use. You agree to:
      </PolicyP>
      <PolicyUL>
        <li>Use the app lawfully and not abuse other campers, staff, or the service.</li>
        <li>Not scrape, copy, or resell our campsite database or content.</li>
        <li>Not attempt to hack, overload, or reverse-engineer the service.</li>
      </PolicyUL>
      <PolicyP>
        We can suspend or delete your account if you break these rules. We'll always try to contact you first.
      </PolicyP>

      <PolicyH2 id="accounts">3. Your account</PolicyH2>
      <PolicyP>
        You're responsible for your sign-in details and anything done from your account. If you share a device,
        remember to log out. If you think someone's accessed your account, email us immediately.
      </PolicyP>
      <PolicyP>
        You can close your account at any time — contact us and we'll delete everything linked to you within 30 days
        (see our <a href="/privacy" className="link-underline text-[#ff4500]">Privacy Policy</a>).
      </PolicyP>

      <PolicyH2 id="community">4. Community rules</PolicyH2>
      <PolicyP>
        CampCompass is a kindness-first space. The full code is in our{" "}
        <a href="/guidelines" className="link-underline text-[#ff4500]">Campfire Code</a>. In short:
        be kind, be helpful, don't hurt people.
      </PolicyP>
      <PolicyUL>
        <li>Personal attacks, harassment, or hate speech are removed.</li>
        <li>Spam, sponsored posts, and fake reviews are removed.</li>
        <li>We moderate transparently — you can appeal any decision by emailing us.</li>
      </PolicyUL>

      <PolicyH2 id="pro">5. CampCompass Pro membership</PolicyH2>
      <PolicyP>
        Pro unlocks extra features (unlimited AI chat, packing-list generator, 14-day weather, partner discount codes,
        and more). It costs <strong>£1.99 per month</strong> after a <strong>14-day free trial</strong>.
      </PolicyP>
      <PolicyUL>
        <li>A payment card is required when you start your trial. This is how Stripe enables auto-renewal.</li>
        <li>Your card will <strong>not</strong> be charged during the 14-day trial.</li>
        <li>On day 15 we charge £1.99 automatically — and every month after that — until you cancel.</li>
        <li>You can cancel any time from the <strong>"Manage subscription"</strong> button on the Pro page. Cancellation stops any future charges. You keep access until the end of the period you've already paid for.</li>
        <li>Each account gets one trial. Re-subscribing after a cancellation skips the trial.</li>
        <li>Payments are processed securely by Stripe. We never see or store your card details.</li>
        <li>UK consumers have the right to cancel within 14 days of paying for a refund, as long as you haven't used the month you're paying for.</li>
      </PolicyUL>

      <PolicyH2 id="affiliate">6. Affiliate links &amp; booking partners</PolicyH2>
      <PolicyCallout>
        Some "Book on Pitchup" buttons in the app are affiliate links. If you book a campsite after clicking one, we may
        earn a small commission <strong>at no extra cost to you</strong>. It helps keep CampCompass free and ad-free. We never
        let commission influence our rankings — in fact we don't rank sponsored campsites at all.
      </PolicyCallout>

      <PolicyH2 id="content">7. Your content (reviews, threads, replies)</PolicyH2>
      <PolicyP>
        Anything you post (reviews, community threads, photos) stays yours. By posting, you give us a worldwide,
        non-exclusive licence to display it inside CampCompass and in promotional materials (with attribution).
      </PolicyP>
      <PolicyP>
        You're responsible for making sure your content is your own and legal to post. We remove anything that
        breaks these terms or UK law.
      </PolicyP>

      <PolicyH2 id="ai">8. Ask Camper (AI trip assistant)</PolicyH2>
      <PolicyP>
        Ask Camper uses Claude (by Anthropic) via our Emergent LLM integration. Answers are AI-generated from our own
        campsite database plus general camping knowledge — they are informative, not professional advice. Always
        double-check safety-critical info (weather, fire bans, access rules) with the campsite itself.
      </PolicyP>

      <PolicyH2 id="liability">9. Our liability</PolicyH2>
      <PolicyP>
        CampCompass is provided "as is". We work hard to keep the app accurate and available, but we can't guarantee
        it. We're not liable for:
      </PolicyP>
      <PolicyUL>
        <li>Third-party decisions (a campsite being fully booked, a closure, a weather change).</li>
        <li>Pitchup bookings — your contract is with the campsite / Pitchup, not us.</li>
        <li>Losses you could have avoided by taking reasonable care.</li>
      </PolicyUL>
      <PolicyP>
        Nothing in these terms limits our liability for things you can't limit by law (fraud, death, personal injury
        caused by our negligence). UK consumer rights are always preserved.
      </PolicyP>

      <PolicyH2 id="changes">10. Changes to these terms</PolicyH2>
      <PolicyP>
        If we update these terms materially, we'll email every registered user at least 14 days before the change
        takes effect. Keep an eye on the "last updated" date at the top of this page.
      </PolicyP>

      <PolicyH2 id="law">11. Governing law &amp; contact</PolicyH2>
      <PolicyP>
        These terms are governed by the laws of England &amp; Wales, and the English courts have jurisdiction. You
        can always contact us at <a href="mailto:hello@camp-compass.uk" className="link-underline text-[#ff4500]">hello@camp-compass.uk</a> — we'd rather talk than litigate.
      </PolicyP>
    </PolicyLayout>
  );
}
